import * as React from 'react';
import * as Markdown from 'react-markdown';
import { firstBy } from 'thenby';
import { DeviceTypes, SessionScheduleItem, Subject, VisitScheduleItem } from '../../misc/api/apiTypes';
import { Device } from '../../misc/api/DeviceLinkApi/Devices';
import * as P230In2itiveApi from '../../misc/api/DeviceLinkApi/P230In2itive';
import Alert, { AlertCategoryEnum } from '../../misc/components/Alert';
import { StudyScheduleVisit } from 'webportalclient';
import { StudyScheduleSession } from 'webportalclient';

export interface DeviceItem {
    SerialNumberPrefix: string;
    SerialNumber: string;
    MediaSerialNumberPrefix: string;
    MediaSerialNumber: string;
    DeviceTypeName: string;
}

interface ConfirmationProps {
    subject: Subject;
    visits: StudyScheduleVisit[];
    sessions: StudyScheduleSession[];
    intendedDeviceDetails: DeviceItem;
    selectedDevice: Device;
    configurationWarning: string;
    isNotConfigured: boolean;
}

export const Confirmation = ({ subject, visits, sessions, intendedDeviceDetails, selectedDevice, configurationWarning, isNotConfigured }: ConfirmationProps) => {
 
    const [differentSubjectOnDevice, setDifferentSubjectOnDevice] = React.useState<boolean>();

    React.useEffect(() => {
        if (selectedDevice.type !== DeviceTypes.In2itiveP230EDiary)
            return;

        const checkForDifferentSubjectOnDevice = async () => {
        const subjectResult = await P230In2itiveApi.GetSubject(selectedDevice.id);

        if (subjectResult !== undefined)
            if (subjectResult && subjectResult.subject.id !== subject.Id){
                setDifferentSubjectOnDevice(true);
            }
            else {
                setDifferentSubjectOnDevice(false);
            }
    }
    checkForDifferentSubjectOnDevice();

    }, [selectedDevice.id, subject.Id]);

    const renderSpecificWarning = () => {
        if (selectedDevice.type === DeviceTypes.In2itiveP230EDiary) {
            return (
                <>
                    <Alert show={differentSubjectOnDevice === true} category={AlertCategoryEnum.Warning}>
                        <Markdown>Device is being configured for a different subject, the device will be wiped.</Markdown>
                    </Alert>
                    <Alert show={subject.EdiarySubject === undefined || subject?.EdiarySubject.Pin === undefined || subject?.EdiarySubject.Pin === ''} category={AlertCategoryEnum.Warning}>
                        <Markdown>There is no PIN configured for this subject.</Markdown>
                    </Alert>
                </>);
        } else {
            return (<Alert show={configurationWarning !== null} category={AlertCategoryEnum.Warning}>
                <Markdown>{configurationWarning}</Markdown>
            </Alert>);
        }
    }

    const renderIsNotConfiguredWarning = () => {
        return (<Alert show={isNotConfigured} category={AlertCategoryEnum.Warning} >
                This session was previously created but the device wasn`&apos;`t configured correctly.<br/>
                Please select the device with same serial numbers listed below to complete the configuration.
            </Alert>);
    }

    const sessionsOrdered = [...sessions].sort(
        firstBy('ordinal')
    );

    let serialBanner = <p className="text-muted">{selectedDevice.serial}</p>;
    if (selectedDevice.type === DeviceTypes.VitaloJAK1) {
        serialBanner = <p className="text-muted">MD{intendedDeviceDetails.SerialNumber} • {`FF${intendedDeviceDetails.MediaSerialNumber}`}</p>;
    }

    return (
        <div>
            <h2 className="text-primary">Confirm</h2>

            {renderSpecificWarning()}
            {selectedDevice.type === DeviceTypes.VitaloJAK1 ? renderIsNotConfiguredWarning() : null}

            <p className="text-muted">The device will be configured with the following&nbsp;details</p>
            <div style={{ background: 'none' }}>
                <div style={{ border: 0 }}>
                    <div className="row">
                        <div style={{ fontSize: '50px', textAlign: 'center' }} className="col-sm-2"><i className="fas fa-user" aria-hidden="true"></i></div>
                        <div className="col-sm-10">
                            <h3>{subject.SubjectNumber}</h3>
                            <p className="text-muted">{subject.Gender ? subject.Gender.Name : ''}</p>
                        </div>
                    </div>
                    {
                        sessionsOrdered.map(session => {
                            const parentVisit = visits.find(x => x.id === session.studyScheduleVisitId);

                            return (
                                <div key={session.id} className="row">
                                    <div style={{ fontSize: '50px', textAlign: 'center' }} className="col-sm-2"><i className="fas fa-calendar" aria-hidden="true"></i></div>
                                    <div className="col-sm-10">
                                        <h3>{parentVisit.label}</h3>
                                        <p className="text-muted">{session.label}</p>
                                    </div>
                                </div>
                            );
                        })
                    }

                    <div className="row">
                        <div style={{ fontSize: '50px', textAlign: 'center' }} className="col-sm-2"><i className="fas fa-hdd" aria-hidden="true"></i></div>
                        <div className="col-sm-10">
                            <h3>{selectedDevice.name}</h3>
                            {serialBanner}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
